// src/components/SnackCrudUsers.jsx
import React, { useEffect, useState } from "react";
import useUsers from "../../market/warehouse-baserow/useUsers";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const SnackCrudUsers = () => {
  const {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
  } = useUsers();

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    Nombre: "",
    Descripcion: "",
    Activo: true,
    Rol: "2-usuario",
    Pin: "",
    Email: "",
    FechaPermitida: "",
    UrlLogo: "",
    Gimnasio: "",
  });

  const [feedback, setFeedback] = useState({ message: "", variant: "" });

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
    } else {
      const lower = searchTerm.toLowerCase();
      const result = users.filter(
        (u) =>
          u.Nombre?.toLowerCase().includes(lower) ||
          u.Email?.toLowerCase().includes(lower)
      );
      setFilteredUsers(result);
    }
  }, [users, searchTerm]);

  const handleNew = () => {
    setEditMode(false);
    setFormData({
      Nombre: "",
      Descripcion: "",
      Activo: true,
      Rol: "2-usuario",
      Pin: "",
      Email: "",
      FechaPermitida: "",
      UrlLogo: "",
      Gimnasio: "",
    });
    setShowModal(true);
  };
  const handleEdit = (user) => {
    setEditMode(true);
    setFormData({ ...user });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSave = async () => {
    const success = editMode
      ? await updateUser(formData.id, formData)
      : await createUser(formData);

    if (success) {
      setFeedback({
        message: editMode
          ? "Usuario actualizado exitosamente"
          : "Usuario creado exitosamente",
        variant: "success",
      });
      fetchUsers();
      setShowModal(false);
    } else {
      setFeedback({
        message: "Error al guardar el usuario",
        variant: "danger",
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("¿Deseas eliminar este usuario?");
    if (!confirm) return;

    const success = await deleteUser(id);
    if (success) {
      setFeedback({ message: "Usuario eliminado", variant: "warning" });
      fetchUsers();
    } else {
      setFeedback({
        message: "No se pudo eliminar el usuario",
        variant: "danger",
      });
    }
  };

  const renderFechaPermitida = (fechaStr) => {
    if (!fechaStr) return "—";

    const hoy = new Date();
    const fecha = new Date(fechaStr);

    const esVigente = fecha >= hoy;

    const estilo = {
      color: esVigente ? "green" : "red",
      fontWeight: "bold",
    };

    const emoji = esVigente ? "✅" : "⚠️";

    return (
      <span style={estilo}>
        {fechaStr} {emoji}
      </span>
    );
  };

  return (
    <div className="container mt-4">
      <h3 className="text-danger fw-bold">Gestión de Usuarios</h3>

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <InputGroup style={{ maxWidth: "300px" }}>
          <Form.Control
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <div className="text-muted">
          Total: {filteredUsers.length} usuario(s)
        </div>

        <Button variant="danger" onClick={handleNew}>
          Crear nuevo usuario
        </Button>
      </div>

      {feedback.message && (
        <Alert
          variant={feedback.variant}
          onClose={() => setFeedback({ message: "", variant: "" })}
          dismissible
        >
          {feedback.message}
        </Alert>
      )}
      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-light text-center">
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Celular</th>
              <th>Activo</th>
              <th>Fecha Permitida</th> {/* ← NUEVO */}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td>{u.Nombre || "—"}</td>
                <td>{u.Email || "—"}</td>
                <td>{u.Descripcion || "—"}</td>
                <td className="text-center">{u.Activo ? "Sí" : "No"}</td>
                <td className="text-center">
                  {renderFechaPermitida(u.FechaPermitida)}
                </td>

                <td className="text-center">
                  <Button
                    size="sm"
                    variant="warning"
                    className="me-2"
                    onClick={() => handleEdit(u)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete(u.id)}
                  >
                    Eliminar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal de creación/edición */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "Editar Usuario" : "Nuevo Usuario"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                name="Nombre"
                value={formData.Nombre}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="Email"
                value={formData.Email}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Rol</Form.Label>
              <Form.Select
                name="Rol"
                value={formData.Rol}
                onChange={handleChange}
              >
                <option value="1-administrador">1-administrador</option>
                <option value="2-usuario">2-usuario</option>
                <option value="3-agencia">3-agencia</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="Descripcion"
                value={formData.Descripcion}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>PIN</Form.Label>
              <Form.Control
                type="text"
                name="Pin"
                value={formData.Pin}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Gimnasio</Form.Label>
              <Form.Control
                type="text"
                name="Gimnasio"
                value={formData.Gimnasio}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>URL Logo</Form.Label>
              <Form.Control
                type="text"
                name="UrlLogo"
                value={formData.UrlLogo}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Fecha Permitida</Form.Label>
              <Form.Control
                type="date"
                name="FechaPermitida"
                value={formData.FechaPermitida}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="Activo"
                name="Activo"
                checked={formData.Activo}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleSave}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SnackCrudUsers;
